import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  addNextBlock,
  formatDraftWeekLine,
  MAX_CYCLE_WEEKS,
  programRoundLabel,
  TETRAD_ROUND_TYPES,
  type Draft,
  type DraftLineOptions,
} from "@heraion/shared";

import { PageSheet } from "../primitives/PageSheet";
import type { UiTheme } from "../theme";
import { ruleOptions } from "./ruleCache";
import { NEXT_BLOCK_ROUNDS } from "./useV3ProgressionActions";
import type { V3Styles } from "./v3Styles";

/** How many exercises the preview shows. */
const PREVIEW_ROWS = 4;

// Flow 9 (#336 slice 5, brief §5.4b): «Add W5-W8». What the next tetrad is,
// how load, reps and effort rows carry on, and the first new round of the
// open day's first exercises, as addNextBlock writes it.
export function NextBlockSheet({
  theme,
  styles,
  draft,
  sIdx,
  lineOptions,
  resolveName,
  onAdd,
  onClose,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  sIdx: number;
  lineOptions: DraftLineOptions;
  resolveName: (id: string, snapshot?: string) => string;
  onAdd: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const local = useMemo(() => makeStyles(theme), [theme]);
  const first = draft.cycleWeeks;
  const unit = lineOptions.cellUnit;
  const next = useMemo(
    () => addNextBlock(draft, { ...ruleOptions(unit), rounds: NEXT_BLOCK_ROUNDS, types: TETRAD_ROUND_TYPES }),
    [draft, unit],
  );
  const from = programRoundLabel(first + 1);
  const to = programRoundLabel(first + NEXT_BLOCK_ROUNDS);
  const slots = next.ok ? (next.draft.sessions[sIdx]?.slots ?? []).slice(0, PREVIEW_ROWS) : [];
  return (
    <PageSheet
      theme={theme}
      visible
      size="compact"
      onRequestClose={onClose}
      title={t("editorV3.rule.blockTitle", { from, to, defaultValue: "Add {{from}}-{{to}}" })}
      cancelLabel={t("program.cancel", { defaultValue: "Cancel" })}
      action={{
        label: t("editorV3.rule.blockAdd", { count: NEXT_BLOCK_ROUNDS, defaultValue: "Add {{count}} rounds" }),
        disabled: !next.ok,
        onPress: () => {
          onAdd();
          onClose();
        },
      }}
    >
      <View style={local.body}>
        {next.ok ? null : (
          // «Add» is off: say why, first.
          <Text style={styles.muted}>
            {t("editorV3.rule.blockFull", {
              max: MAX_CYCLE_WEEKS,
              defaultValue: "A program holds at most {{max}} rounds: there is no room for another tetrad.",
            })}
          </Text>
        )}
        <Text style={styles.muted}>
          {t("editorV3.rule.blockShape", {
            defaultValue: "One more tetrad: two normal rounds, one test round, one deload round.",
          })}
        </Text>
        <Text style={styles.muted}>
          {t("editorV3.rule.blockCarry", { defaultValue: "Load and rep rows carry on from where they were." })}
        </Text>
        <Text style={styles.muted}>
          {t("editorV3.rule.blockEffort", {
            defaultValue: "Effort rows repeat their shape. The load rises with the MAX measured in the test round.",
          })}
        </Text>
        {slots.length > 0 ? (
          <View style={styles.ladder}>
            {slots.map((s) => (
              <View key={s.id} style={styles.ladderRow} accessible>
                <Text style={styles.lensNameText} numberOfLines={2}>
                  {resolveName(s.exerciseId, s.exerciseName)}
                </Text>
                <Text style={styles.roundLine}>
                  {`${from} ${formatDraftWeekLine(s, first, lineOptions) ?? t("editorV3.notInRound", { defaultValue: "Not in this round" })}`}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </PageSheet>
  );
}

function makeStyles(theme: UiTheme) {
  return StyleSheet.create({ body: { padding: theme.spacing.s5, gap: theme.spacing.s3 } });
}
